import { useEffect, useMemo, useRef, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import toast from "react-hot-toast"
import { onSnapshot, doc } from "firebase/firestore"
import {
  ArrowLeft, ArrowRight, Bookmark, Briefcase, Clock, Code2,
  DollarSign, ExternalLink, Globe, MapPin, Ban,
} from "lucide-react"
import { db } from "../firebaseConfig"
import { useAuth } from "../contexts/AuthContext"
import { useUser } from "../contexts/UserContext"
import Sidebar from "../components/Sidebar"
import ApplyAnimation from "../components/ApplyAnimation"
import { daysAgoLabel, parseSalary, initials } from "../components/JobCard"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const JOB_API = "https://jobscanner-e1dd4.ue.r.appspot.com/network/job"
const APPLICATIONS_API = "https://jobscanner-e1dd4.ue.r.appspot.com/applications"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

const DASHBOARD_TAB_ROUTES = {
  console: "/dashboard/home",
  applications: "/dashboard/applications",
  wild: "/dashboard/wild",
  inbox: "/dashboard/inbox",
  profile: "/dashboard/profile",
}

function FactRow({ icon, label }) {
  if (!label) return null
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: FF }}>
      <span style={{ display: "flex", color: "#52525b", flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: "#18181b" }}>{label}</span>
    </div>
  )
}

function Sk({ w = "100%", h = 14 }) {
  return <div style={{ width: w, height: h, borderRadius: 6, background: "#f0f0f0", animation: "djob-shimmer 1.4s ease-in-out infinite" }} />
}

export default function DashboardJobPage() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { userData } = useUser()
  const userId = user?.uid
  const applyAnimRef = useRef(null)

  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [applying, setApplying] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null); setJob(null)
    fetch(`${JOB_API}/${jobId}`)
      .then(r => { if (!r.ok) throw new Error("not_found"); return r.json() })
      .then(data => {
        if (cancelled) return
        if (!data.success || !data.listing) throw new Error("not_found")
        setJob(data.listing)
      })
      .catch(() => { if (!cancelled) setError("Job not found or no longer available.") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [jobId])

  useEffect(() => {
    if (!userId || !jobId) return
    const ref = doc(db, "users", userId, "saved_jobs", jobId)
    const unsub = onSnapshot(ref, snap => setSaved(snap.exists()))
    return () => unsub()
  }, [userId, jobId])

  const company = job?.company || "—"
  const title = job?.role || "—"
  const descriptionRaw = job?.description || ""
  const isHtml = /<[a-z][\s\S]*>/i.test(descriptionRaw)
  const skills = Array.isArray(job?.skills) ? job.skills : []
  const sal = job ? parseSalary(job.salary) : null
  const level = job?.seniority_key && job.seniority_key !== "UNKNOWN"
    ? job.seniority_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null
  const ago = job ? daysAgoLabel(job) : ""
  const sourceLabel = job?.source ? (job.source === "Custom" ? `${company} Careers` : job.source) : null
  const loc = job?.canonical_location_id || job?.location || null
  const workType = job?.work_type || null

  const domain = useMemo(() => {
    if (!job?.company) return null
    return job.company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
  }, [job?.company])

  const handleTabChange = (tab) => {
    const to = DASHBOARD_TAB_ROUTES[tab]
    if (to) navigate(to)
  }

  const handleApply = async () => {
    if (applying || !job) return
    const jobUrl = job.job_url || job.jobUrl
    if (!jobUrl) { toast.error("This job has no application URL"); return }
    if (!userId) { toast.error("Please sign in before applying"); return }

    setApplying(true)
    applyAnimRef.current?.play({ jobTitle: title, company })
    try {
      const res = await fetch(APPLICATIONS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId, jobUrl, jobTitle: title, company,
          logoUrl: job.company_logo || "",
          description: descriptionRaw,
          fcmToken: "",
        }),
      })
      if (res.status !== 201) throw new Error(`Application API error ${res.status}`)
      await res.json()
    } catch (err) {
      console.error("Failed to create application:", err)
      toast.error("Something went wrong starting Agent Apply")
    } finally {
      setApplying(false)
    }
  }

  const handleSignOut = async () => {
    try { await signOut() } catch (err) { console.error(err) }
    navigate("/")
  }

  const back = () => {
    if (location.key !== "default") navigate(-1)
    else navigate("/dashboard/home")
  }

  return (
    <div style={{
      display: "flex", height: "100dvh", width: "100%",
      background: "#ffffff", color: "#18181b",
      fontFamily: FF, overflow: "hidden",
    }}>
      <style>{`
        @keyframes djob-shimmer { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes djob-fade-up { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .djob-scroll::-webkit-scrollbar { width: 4px }
        .djob-scroll::-webkit-scrollbar-track { background: transparent }
        .djob-scroll::-webkit-scrollbar-thumb { background: #d4d4d8; border-radius: 2px }
        .djob-body { font-size: 13.5px; color: #3f3f46; line-height: 1.7; font-family: ${FF}; word-break: break-word; }
        .djob-body h1,.djob-body h2,.djob-body h3,.djob-body h4 { font-size: 14px; font-weight: 700; color: #18181b; margin: 20px 0 8px; letter-spacing: -0.01em; line-height: 1.3; }
        .djob-body p  { margin: 0 0 12px }
        .djob-body ul,.djob-body ol { padding-left: 20px; margin: 0 0 14px }
        .djob-body li { margin-bottom: 6px }
        .djob-body strong,.djob-body b { color: #18181b; font-weight: 600 }
        .djob-body a { color: #4338ca; text-decoration: none }
        .djob-body a:hover { text-decoration: underline }
        .djob-apply { display: inline-flex; align-items: center; gap: 6px; height: 40px; padding: 0 20px; border-radius: 9999px; background: #121212; color: #F5F0E6; border: none; cursor: pointer; font-size: 13px; font-weight: 600; letter-spacing: -0.005em; transition: background 0.18s ease; white-space: nowrap; }
        .djob-apply:hover { background: #000; }
        .djob-apply:disabled { opacity: 0.6; cursor: default; }
        .djob-apply-arrow { transition: transform 0.2s ease; }
        .djob-apply:hover .djob-apply-arrow { transform: translateX(2px); }
        .djob-ghost { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; cursor: pointer; padding: 8px 10px; border-radius: 8px; font-size: 12px; font-weight: 500; color: #52525b; font-family: ${FF}; transition: background 0.12s; }
        .djob-ghost:hover { background: #f4f4f5; }
      `}</style>

      {/* Sidebar */}
      <div style={{ display: "flex", flexShrink: 0 }}>
        <Sidebar
          activeTab="console"
          onTabChange={handleTabChange}
          user={user}
          userData={userData}
          onSignOut={handleSignOut}
        />
      </div>

      {/* Content column */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top bar: back on left, all actions on right */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 28px",
          background: "#ffffff",
          flexShrink: 0, gap: 8,
        }}>
          <button
            onClick={back}
            className="djob-ghost"
            style={{ padding: "6px 10px 6px 6px" }}
          >
            <ArrowLeft size={14} strokeWidth={2.2} />
            Back
          </button>

          {job && !loading && !error && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => (job.job_url || job.jobUrl) && window.open(job.job_url || job.jobUrl, "_blank")}
                className="djob-ghost"
              >
                <ExternalLink size={13} strokeWidth={2} />
                View original posting
              </button>
              <button
                onClick={() => toast("Not interested — got it")}
                className="djob-ghost"
                style={{ color: "#71717a" }}
              >
                <Ban size={13} strokeWidth={2} />
                Not Interested
              </button>
              <button
                onClick={() => toast("Save coming soon")}
                aria-label={saved ? "Saved" : "Save"}
                style={{
                  width: 38, height: 38, borderRadius: 9999,
                  background: "#f4f4f5",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginLeft: 4,
                  transition: "background 0.12s",
                }}
              >
                <Bookmark size={14} strokeWidth={2}
                  fill={saved ? "#18181b" : "none"}
                  stroke="#18181b" />
              </button>
              <button
                className="djob-apply"
                onClick={handleApply}
                disabled={applying}
                style={{ marginLeft: 4 }}
              >
                {applying ? "Starting…" : "Apply"}
              </button>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div
          className="djob-scroll"
          style={{
            flex: 1, overflowY: "auto",
            padding: "8px 28px 28px",
            animation: "djob-fade-up 0.2s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 820 }}>
              <Sk w="40%" h={22} />
              <Sk w="25%" h={14} />
              <div style={{ height: 20 }} />
              {[...Array(8)].map((_, i) => <Sk key={i} w={i % 3 === 2 ? "55%" : "90%"} h={13} />)}
            </div>
          ) : error ? (
            <div style={{ padding: "48px 0" }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#18181b" }}>{error}</p>
              <button
                onClick={() => navigate("/dashboard/home")}
                className="djob-apply"
                style={{ marginTop: 16, height: 36 }}
              >
                Back to jobs
              </button>
            </div>
          ) : job ? (
            <div style={{ maxWidth: 820 }}>
              {/* Header: big logo + title + company */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                <div style={{
                  width: 60, height: 60, borderRadius: 12,
                  background: "#f4f4f5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, overflow: "hidden",
                }}>
                  {domain ? (
                    <img
                      src={`https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}`}
                      alt={company}
                      onError={e => { e.currentTarget.style.display = "none" }}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#52525b" }}>
                      {initials(company)}
                    </span>
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h1 style={{
                    margin: 0, fontSize: 22, fontWeight: 700,
                    color: "#18181b", lineHeight: 1.2,
                    letterSpacing: "-0.025em",
                  }}>
                    {title}
                  </h1>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#52525b", fontWeight: 500 }}>
                    {company}{ago ? ` · ${ago}` : ""}
                  </p>
                </div>
              </div>

              {/* Facts grid */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px",
                marginBottom: 28,
              }}>
                <FactRow icon={<MapPin size={14} strokeWidth={2} />} label={loc} />
                <FactRow icon={<DollarSign size={14} strokeWidth={2} />} label={sal} />
                <FactRow icon={<Briefcase size={14} strokeWidth={2} />} label={level} />
                <FactRow icon={<Globe size={14} strokeWidth={2} />} label={workType} />
                <FactRow icon={<Clock size={14} strokeWidth={2} />} label={job.experience_min != null ? `${job.experience_min}+ yrs exp` : "Full Time"} />
                <FactRow icon={<Code2 size={14} strokeWidth={2} />} label={sourceLabel} />
              </div>

              {/* Skills */}
              {skills.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#18181b", letterSpacing: "-0.01em" }}>
                    Skills &amp; Technologies
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {skills.map((s, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "5px 10px", borderRadius: 3,
                        fontSize: 12, fontWeight: 500, fontFamily: FF,
                        color: "#5b21b6", background: "#ede9fe",
                        whiteSpace: "nowrap",
                      }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Inline Apply CTA below skills */}
              <div style={{ marginBottom: 28 }}>
                <button
                  className="djob-apply djob-apply-inline"
                  onClick={handleApply}
                  disabled={applying}
                >
                  {applying ? "Starting…" : "Apply Now"}
                  <ArrowRight size={14} strokeWidth={2.4} className="djob-apply-arrow" />
                </button>
              </div>

              {/* Description */}
              <div>
                <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#18181b", letterSpacing: "-0.01em" }}>
                  Description
                </p>
                {isHtml ? (
                  <div className="djob-body" dangerouslySetInnerHTML={{ __html: descriptionRaw }} />
                ) : (
                  <p className="djob-body" style={{ whiteSpace: "pre-wrap" }}>{descriptionRaw}</p>
                )}
              </div>
            </div>
          ) : null}
        </div>

      </div>

      <ApplyAnimation ref={applyAnimRef} />
    </div>
  )
}
